/* ═══════════════════════════════════════════════════════════════
   zone-layout — the ONE layout brain for L/C/R header/footer zones.

   Word output uses tab-stop paragraphs, never tables (Josh's law).
   A tab line cannot wrap, so overflow must be decided BEFORE Word
   sees the line. This module makes every layout decision from real
   measurements and returns visual rows whose contents are GUARANTEED
   to fit side by side — collision is structurally impossible:

     · each column is a CONTINUOUS STACK: it packs its own visual
       lines top-down, and one column's wrapping never opens a gap
       in another (Josh's law, 2026-08-12) — rows are formed by
       zipping the three stacks
     · the center span is symmetric about the page middle (a center
       tab centers there), bounded by the widest outer line
     · anything that cannot share a row gets a row of its own

   BOTH consumers run this SAME code with their own measurer:
     · word-engine.ts (export)  — font-file metrics (font-metrics.ts)
     · SpecFormattedView (preview) — canvas measureText
   One brain, two renderers ⇒ what the preview shows IS what Word
   lays out. (The v2.14.28 demo failure was exactly this split.)

   Pure TypeScript: no Node, no DOM. Measurement is injected.
   ═══════════════════════════════════════════════════════════════ */

import { placeholderId } from './forma-placeholders';

/** Measures one line's tokens → width in twips (1pt = 20 twips). */
export type ZoneMeasure = (tokens: string[]) => number;

/** One visual row. Any column may be empty; occupied columns are
 *  guaranteed to fit beside each other within the layout width. */
export interface ZoneRow {
  L: string[];
  C: string[];
  R: string[];
}

interface VisLine {
  toks: string[];
  /** true = must not share a row (single fragment wider than its span) */
  exclusive: boolean;
}

/** ~0.08" clearance between columns on a shared row. */
export const ZONE_GAP_TWIPS = 120;

/** The center span never shrinks below this share of the width —
 *  outer columns wrap before they can starve the center. */
const MIN_CENTER_SHARE = 0.25;

// ─── token helpers (shared with both renderers) ───

export function parseTokenMods(raw: string): { base: string; mods: Set<string> } {
  const pipeIdx = raw.lastIndexOf('|');
  if (pipeIdx <= 0) return { base: raw, mods: new Set<string>() };
  let modStr = raw.slice(pipeIdx + 1);
  if (modStr.endsWith('}')) modStr = modStr.slice(0, -1);
  const cands = modStr
    .toLowerCase()
    .split(',')
    .map((s: string) => s.trim());
  const isValid = (m: string) =>
    ['bold', 'caps', 'italic', 'underline', 'titlecase', 'sentencecase'].includes(m) ||
    (m.startsWith('color:') && /^[0-9a-fA-F]{6}$/.test(m.slice(6))) ||
    /^s[ab]:\d+(\.\d+)?$/.test(m) ||
    /^size:\d+(\.\d+)?$/.test(m); // size:N — per-token font size override, in points
  if (!cands.every(isValid)) return { base: raw, mods: new Set<string>() };
  const base = raw.slice(0, pipeIdx) + (raw.endsWith('}') ? '}' : '');
  return { base, mods: new Set<string>(cands) };
}

/** Title Case — first letter of every word capitalized, the rest lowercase.
 *  Word-standard ("Change Case → Capitalize Each Word"). Needed as a real
 *  string transform (not CSS `text-transform: capitalize`) because source
 *  text in this app is routinely already stored ALL CAPS ({SpecName} etc.)
 *  — CSS `capitalize` never lowercases, so it would leave "SPECIFICATION
 *  NAME" untouched instead of producing "Specification Name". */
export function toTitleCase(text: string): string {
  return text.toLowerCase().replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/** Sentence case — first letter of the string and of each sentence (after
 *  `. ! ?` + whitespace) capitalized, rest lowercase. Same "needs a real
 *  transform" reasoning as Title Case — CSS has no sentence-case value at
 *  all, and OOXML has no run property for either. */
export function toSentenceCase(text: string): string {
  return text.toLowerCase().replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, (m) => m.toUpperCase());
}

/** Apply a token's case mod — `caps` / `titlecase` / `sentencecase` are
 *  mutually exclusive (the format menu enforces that when setting them);
 *  this just applies whichever is present, in that priority order, to
 *  already-resolved literal text. No mod present → text unchanged. One
 *  brain so the canvas, the "real preview," and the .docx export agree. */
export function applyCaseMod(text: string, mods: Set<string>): string {
  if (mods.has('caps')) return text.toUpperCase();
  if (mods.has('titlecase')) return toTitleCase(text);
  if (mods.has('sentencecase')) return toSentenceCase(text);
  return text;
}

/** Read a token's `size:N` mod (points), or undefined when absent —
 *  the zone's base font size applies. Shared by every measurer/emitter so
 *  "what size does this run print at" is answered in exactly one place. */
export function modSizePt(mods: Set<string>): number | undefined {
  for (const m of mods) {
    if (m.startsWith('size:')) {
      const v = parseFloat(m.slice(5));
      if (v > 0) return v;
    }
  }
  return undefined;
}

export function parseTokenSpacing(raw: string): { before: number; after: number } {
  const out = { before: 0, after: 0 };
  if (!raw || raw === '\n') return out;
  const { mods } = parseTokenMods(raw);
  for (const m of mods) {
    if (m.startsWith('sb:')) out.before = Math.max(out.before, parseFloat(m.slice(3)) || 0);
    else if (m.startsWith('sa:')) out.after = Math.max(out.after, parseFloat(m.slice(3)) || 0);
  }
  return out;
}

/** Split a token array into explicit lines at '\n' tokens. */
export function splitTokenLines(tokens: string[]): string[][] {
  if (!tokens || tokens.length === 0) return [];
  const lines: string[][] = [[]];
  tokens.forEach((t) => {
    if (t === '\n') lines.push([]);
    else lines[lines.length - 1].push(t);
  });
  return lines;
}

/** Expand embedded '\n' inside literal text tokens into '\n' elements. */
export function normalizeTokens(tokens: string[]): string[] {
  if (!tokens || tokens.length === 0) return [];
  const result: string[] = [];
  for (const t of tokens) {
    if (t === '\n') {
      result.push(t);
      continue;
    }
    const { base } = parseTokenMods(t);
    if (base.startsWith('{') && base.endsWith('}')) {
      result.push(t);
      continue;
    }
    if (t.includes('\n')) {
      const parts = t.split('\n');
      parts.forEach((part, i) => {
        if (part.length > 0) result.push(part);
        if (i < parts.length - 1) result.push('\n');
      });
    } else {
      result.push(t);
    }
  }
  return result;
}

/** Spacing signature of a line — rows can only share a paragraph when
 *  their columns agree on before/after spacing. */
export function spacingSig(toks: string[]): string {
  let b = 0;
  let a = 0;
  toks.forEach((t) => {
    const s = parseTokenSpacing(t);
    b = Math.max(b, s.before);
    a = Math.max(a, s.after);
  });
  return b + ':' + a;
}

/** Drop sb:/sa: mods from a token (fragments of a wrapped line must not
 *  repeat their source line's vertical spacing on every row). */
function stripSpacingMods(token: string, keepSb: boolean, keepSa: boolean): string {
  if (keepSb && keepSa) return token;
  const { base, mods } = parseTokenMods(token);
  if (mods.size === 0) return token;
  const kept = [...mods].filter((m) => {
    if (m.startsWith('sb:')) return keepSb;
    if (m.startsWith('sa:')) return keepSa;
    return true;
  });
  if (kept.length === mods.size) return token;
  if (kept.length === 0) return base;
  if (base.startsWith('{') && base.endsWith('}')) {
    return base.slice(0, -1) + '|' + kept.join(',') + '}';
  }
  return base + '|' + kept.join(',');
}

// ─── token → measurable text (shared by engine measure + preview measure) ───

/** Everything token resolution needs, renderer- and engine-agnostic. */
export interface TokenTextCtx {
  specNumber?: string;
  specName?: string;
  endOfSectionText?: string;
  dateFormat?: string;
  /** resolved project/client/issuance values, keyed like "ProjectToken:x" / "Project.Name" */
  project?: Record<string, string>;
  /** What each `{P#}` placeholder falls back to when the project hasn't bound
   *  it — the imported template's memory, keyed "P1"/"IssuanceDate". Keeps a
   *  master's preview measuring the same width its .dotx export prints. */
  placeholderMemories?: Record<string, string>;
}

/** The text a token renders as — for MEASUREMENT. Dynamic Word fields use
 *  worst-typical placeholders (page "99") so estimates stay conservative.
 *  Multi-line values are reduced to their widest line by the caller. */
export function tokenMeasureText(token: string, ctx: TokenTextCtx): string {
  if (token === '\n') return '';
  const { base, mods } = parseTokenMods(token);
  let text: string;
  switch (base) {
    case '{Page}':
    case '{NumPages}':
      text = '99';
      break;
    case '{PageOfPages}':
      text = 'Page 99 of 99';
      break;
    case '{Date}':
      text = (ctx.dateFormat || 'M/d/yyyy').replace(/[MdyHhms]/g, '0');
      break;
    case '{SpecNumber}':
      text = ctx.specNumber || '';
      break;
    case '{SpecName}':
      text = ctx.specName || '';
      break;
    case '{SpecNumName}':
      text = (ctx.specNumber || '') + ' - ' + (ctx.specName || '');
      break;
    case '{EndOfSection}':
      text = ctx.endOfSectionText || 'END OF SECTION';
      break;
    case '{Space}':
      text = ' ';
      break;
    default: {
      if (base.startsWith('{Image:')) return ''; // measured separately by the caller
      const phId = placeholderId(base);
      if (phId) {
        text = String(
          (ctx.project && ctx.project[phId]) ||
            (ctx.placeholderMemories && ctx.placeholderMemories[phId]) ||
            ''
        );
      } else if (
        base.startsWith('{ProjectToken:') ||
        /^\{(Project|Client|Issuance|Team|Deliverable)(\.[A-Za-z]+)?\}$/.test(base)
      ) {
        const key = base.slice(1, -1);
        text = String((ctx.project && ctx.project[key]) || '');
      } else {
        text = base;
      }
    }
  }
  text = applyCaseMod(text, mods);
  return text;
}

// ─── wrapping ───

/** Greedy head-that-fits: pack tokens (splitting literal text at word
 *  boundaries, format mods preserved) until `avail` twips are used.
 *  Field tokens are atomic. Empty head ⇒ nothing fits. */
export function splitTokensToFit(
  tokens: string[],
  avail: number,
  measure: ZoneMeasure
): { head: string[]; rest: string[] } {
  const head: string[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (measure([...head, t]) <= avail) {
      head.push(t);
      continue;
    }
    const { base } = parseTokenMods(t);
    const isField = base.startsWith('{') && base.endsWith('}');
    if (!isField) {
      const modSuffix = t.slice(base.length);
      const words = base.split(' ');
      let take = '';
      for (const word of words) {
        const cand = take ? take + ' ' + word : word;
        if (measure([...head, cand + modSuffix]) <= avail) take = cand;
        else break;
      }
      if (take && take.length < base.length) {
        head.push(take + modSuffix);
        const restText = base.slice(take.length).replace(/^ /, '');
        return { head, rest: [restText + modSuffix, ...tokens.slice(i + 1)] };
      }
    }
    return { head, rest: tokens.slice(i) };
  }
  return { head, rest: [] };
}

/** Wrap ONE source line into visual lines within `span` twips.
 *  Never loops: a fragment that cannot fit even alone comes out as an
 *  `exclusive` line (it gets a full row to itself downstream). */
function wrapLine(source: string[], span: number, measure: ZoneMeasure): VisLine[] {
  if (source.length === 0) return [];
  if (measure(source) <= span) return [{ toks: source, exclusive: false }];
  const out: VisLine[] = [];
  let remaining = source;
  let isFirst = true;
  while (remaining.length > 0) {
    const { head, rest } = splitTokensToFit(remaining, span, measure);
    let frag: string[];
    let exclusive = false;
    if (head.length > 0) {
      frag = head;
      remaining = rest;
    } else {
      // nothing fits — take one atomic token as an exclusive full row
      frag = [remaining[0]];
      remaining = remaining.slice(1);
      exclusive = true;
    }
    const isLast = remaining.length === 0;
    out.push({
      toks: frag.map((t) => stripSpacingMods(t, isFirst, isLast)),
      exclusive,
    });
    isFirst = false;
  }
  return out;
}

// ─── the layout ───

/** A visual line queued in its column's stack. */
interface QLine {
  toks: string[];
  exclusive: boolean;
  /** source-line index — earliest source wins row conflicts */
  src: number;
  /** spacingSig of this visual line (computed after fragment stripping) */
  sig: string;
  /** deliberate empty source line: takes one row slot in THIS column only */
  spacer: boolean;
}

/**
 * Lay out three zones into visual rows for a `w`-twip-wide area.
 *
 * Inputs are RAW zone token arrays (with '\n' line tokens). Tokens whose
 * resolved value is multi-line must be expanded by the caller before this
 * runs (the caller owns token resolution; layout only measures).
 *
 * Josh's law (2026-08-12): each column is a CONTINUOUS STACK. One
 * column's wrapping must never open a gap in another. Left/center/right
 * each pack their own visual lines top-down; rows are formed by zipping
 * the three stacks.
 */
export function layoutZones(
  leftTokens: string[],
  centerTokens: string[],
  rightTokens: string[],
  w: number,
  measure: ZoneMeasure,
  gap: number = ZONE_GAP_TWIPS
): ZoneRow[] {
  const leftLines = splitTokenLines(normalizeTokens(leftTokens));
  const centerLines = splitTokenLines(normalizeTokens(centerTokens));
  const rightLines = splitTokenLines(normalizeTokens(rightTokens));
  const srcLines = Math.max(leftLines.length, centerLines.length, rightLines.length);
  if (srcLines === 0) return [];

  // Phase 1 — build each column's stack of visual lines across ALL source
  // lines. Span/cap math stays per source line, so every wrap decision is
  // identical to the aligned-band era; only row pairing changed.
  const qL: QLine[] = [];
  const qC: QLine[] = [];
  const qR: QLine[] = [];
  const enqueue = (q: QLine[], vis: VisLine[], src: number) =>
    vis.forEach((v) =>
      q.push({ toks: v.toks, exclusive: v.exclusive, src, sig: spacingSig(v.toks), spacer: false })
    );
  const spacer = (src: number): QLine => ({
    toks: [],
    exclusive: false,
    src,
    sig: '',
    spacer: true,
  });

  for (let i = 0; i < srcLines; i++) {
    const L = leftLines[i] || [];
    const C = centerLines[i] || [];
    const R = rightLines[i] || [];
    const hasC = C.length > 0;

    let visL: VisLine[] = [];
    let visC: VisLine[] = [];
    let visR: VisLine[] = [];
    if (hasC && L.length === 0 && R.length === 0) {
      // Center alone on this source line — the full width is its span.
      visC = wrapLine(C, w, measure);
    } else if (hasC) {
      // Outer columns may not squeeze the center below its minimum share.
      const minCenter = Math.floor(w * MIN_CENTER_SHARE);
      const capOuter = Math.max(Math.floor((w - minCenter) / 2) - gap, Math.floor(w * 0.15));
      visL = wrapLine(L, capOuter, measure);
      visR = wrapLine(R, capOuter, measure);
      const widest = (vis: VisLine[]) =>
        vis.reduce((m, v) => (v.exclusive ? m : Math.max(m, measure(v.toks))), 0);
      const outerMax = Math.max(widest(visL), widest(visR));
      const centerSpan = w - 2 * (outerMax + gap);
      visC = wrapLine(C, centerSpan, measure);
    } else {
      // Two columns: the wider side gets the slack, both capped so a
      // shared row normally fits (the zip's width check is the net for
      // cross-source pairings and the both-long case).
      const Lw = measure(L);
      const Rw = measure(R);
      const half = Math.floor((w - gap) / 2);
      const capL = L.length ? Math.max(half, w - gap - Math.min(Rw, half)) : 0;
      const capR = R.length ? Math.max(half, w - gap - Math.min(Lw, half)) : 0;
      visL = wrapLine(L, capL, measure);
      visR = wrapLine(R, capR, measure);
    }

    // A deliberate empty source line ('' between \n\n) stays a spacer in
    // THAT column only — it takes one row slot there (renders empty)
    // without forcing the other columns to skip. When every column has
    // an empty source line, the spacers zip into one fully empty row.
    if (L.length) enqueue(qL, visL, i);
    else if (i < leftLines.length) qL.push(spacer(i));
    if (C.length) enqueue(qC, visC, i);
    else if (i < centerLines.length) qC.push(spacer(i));
    if (R.length) enqueue(qR, visR, i);
    else if (i < rightLines.length) qR.push(spacer(i));
  }

  // Phase 2 — zip the stacks into rows. A row takes the head of every
  // non-empty stack UNLESS:
  //   (a) a head is exclusive — it rides alone (L, C, R priority);
  //   (b) spacing signatures conflict — only matching-sig heads share,
  //       and the head(s) from the earliest source line go first;
  //   (c) the combined widths + gaps overflow `w` — the earliest-source
  //       head(s) ride first.
  // No other case may hold a column back while rendering its slot empty
  // (Josh's law, 2026-08-12).
  const rows: ZoneRow[] = [];
  while (qL.length > 0 || qC.length > 0 || qR.length > 0) {
    if (qL.length > 0 && qL[0].exclusive) {
      rows.push({ L: qL.shift()!.toks, C: [], R: [] });
      continue;
    }
    if (qC.length > 0 && qC[0].exclusive) {
      rows.push({ L: [], C: qC.shift()!.toks, R: [] });
      continue;
    }
    if (qR.length > 0 && qR[0].exclusive) {
      rows.push({ L: [], C: [], R: qR.shift()!.toks });
      continue;
    }

    type Cand = { col: 'L' | 'C' | 'R'; q: QLine[]; e: QLine };
    let cands: Cand[] = [];
    if (qL.length > 0) cands.push({ col: 'L', q: qL, e: qL[0] });
    if (qC.length > 0) cands.push({ col: 'C', q: qC, e: qC[0] });
    if (qR.length > 0) cands.push({ col: 'R', q: qR, e: qR[0] });

    // (b) spacing agreement — spacers are neutral: an empty slot carries
    // no spacing of its own and may sit beside anything.
    const sigsOf = (list: Cand[]) => new Set(list.filter((c) => !c.e.spacer).map((c) => c.e.sig));
    if (sigsOf(cands).size > 1) {
      const minSrc = Math.min(...cands.map((c) => c.e.src));
      const lead =
        cands.find((c) => c.e.src === minSrc && !c.e.spacer) || cands.find((c) => !c.e.spacer)!;
      cands = cands.filter((c) => c.e.spacer || c.e.sig === lead.e.sig);
    }

    // (c) width — a shared row must fit. Cross-source pairings were not
    // covered by the per-source span math, so verify here; on overflow
    // the earliest-source head(s) ride first, and a same-source overflow
    // keeps heads greedily in L, C, R order (at least one always rides).
    const fits = (list: Cand[]) => {
      const occ = list.filter((c) => c.e.toks.length > 0);
      if (occ.length <= 1) return true;
      const total = occ.reduce((s, c) => s + measure(c.e.toks), 0) + (occ.length - 1) * gap;
      return total <= w;
    };
    if (!fits(cands)) {
      const minSrc = Math.min(...cands.map((c) => c.e.src));
      cands = cands.filter((c) => c.e.src === minSrc);
      if (!fits(cands)) {
        const kept: Cand[] = [];
        for (const c of cands) if (fits([...kept, c])) kept.push(c);
        cands = kept.length > 0 ? kept : [cands[0]];
      }
    }

    const row: ZoneRow = { L: [], C: [], R: [] };
    for (const c of cands) row[c.col] = c.q.shift()!.toks;
    rows.push(row);
  }

  return rows;
}

/* ═══════════════════════════════════════════════════════════════
   Indexed layout — ADDITIVE (Josh, 2026-08-13). The plain `layoutZones`
   above is untouched: word-engine.ts and every existing test keep
   calling it and get byte-identical output.

   `layoutZonesIndexed` runs the SAME wrap/pack/zip algorithm — mirrored,
   not shared, so `layoutZones`'s output can never drift from a shared-code
   refactor risk — but carries each visual-row token's ORIGIN INDEX (its
   position in the raw zone array the caller passed in, before '\n'
   expansion/splitting) alongside it. That is what lets the paper attach a
   `token:<zoneKey>:<idx>` key to every rendered fragment, including a
   literal that word-wrapped across several visual lines — the whole
   reason the on-sheet context menu (DESIGN.md §4) can reach a wrapped
   token at all. A token whose OWN value contains a literal '\n' (rare:
   hand-typed multi-line custom text) still resolves to its one true
   origin index for every piece — normalizeTokens' expansion carries the
   index through unchanged. ═══════════════════════════════════════════ */

/** One row's per-column origin indices, parallel to `ZoneRow.L/C/R`.
 *  `null` marks a slot that isn't one raw input token (there are none
 *  today — kept for forward compatibility with synthetic slots). */
export interface ZoneRowSrc {
  L: (number | null)[];
  C: (number | null)[];
  R: (number | null)[];
}

interface IdxTok {
  tok: string;
  /** index into the RAW token array this caller passed to layoutZonesIndexed */
  src: number;
}

function normalizeIdxTokens(tokens: IdxTok[]): IdxTok[] {
  if (!tokens || tokens.length === 0) return [];
  const result: IdxTok[] = [];
  for (const t of tokens) {
    if (t.tok === '\n') {
      result.push(t);
      continue;
    }
    const { base } = parseTokenMods(t.tok);
    if (base.startsWith('{') && base.endsWith('}')) {
      result.push(t);
      continue;
    }
    if (t.tok.includes('\n')) {
      const parts = t.tok.split('\n');
      parts.forEach((part, i) => {
        if (part.length > 0) result.push({ tok: part, src: t.src });
        if (i < parts.length - 1) result.push({ tok: '\n', src: t.src });
      });
    } else {
      result.push(t);
    }
  }
  return result;
}

function splitIdxTokenLines(tokens: IdxTok[]): IdxTok[][] {
  if (!tokens || tokens.length === 0) return [];
  const lines: IdxTok[][] = [[]];
  tokens.forEach((t) => {
    if (t.tok === '\n') lines.push([]);
    else lines[lines.length - 1].push(t);
  });
  return lines;
}

function splitIdxTokensToFit(
  tokens: IdxTok[],
  avail: number,
  measure: ZoneMeasure
): { head: IdxTok[]; rest: IdxTok[] } {
  const head: IdxTok[] = [];
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (measure([...head.map((h) => h.tok), t.tok]) <= avail) {
      head.push(t);
      continue;
    }
    const { base } = parseTokenMods(t.tok);
    const isField = base.startsWith('{') && base.endsWith('}');
    if (!isField) {
      const modSuffix = t.tok.slice(base.length);
      const words = base.split(' ');
      let take = '';
      for (const word of words) {
        const cand = take ? take + ' ' + word : word;
        if (measure([...head.map((h) => h.tok), cand + modSuffix]) <= avail) take = cand;
        else break;
      }
      if (take && take.length < base.length) {
        head.push({ tok: take + modSuffix, src: t.src });
        const restText = base.slice(take.length).replace(/^ /, '');
        return { head, rest: [{ tok: restText + modSuffix, src: t.src }, ...tokens.slice(i + 1)] };
      }
    }
    return { head, rest: tokens.slice(i) };
  }
  return { head, rest: [] };
}

interface VisLineIdx {
  toks: IdxTok[];
  exclusive: boolean;
}

function wrapIdxLine(source: IdxTok[], span: number, measure: ZoneMeasure): VisLineIdx[] {
  if (source.length === 0) return [];
  if (measure(source.map((s) => s.tok)) <= span) return [{ toks: source, exclusive: false }];
  const out: VisLineIdx[] = [];
  let remaining = source;
  let isFirst = true;
  while (remaining.length > 0) {
    const { head, rest } = splitIdxTokensToFit(remaining, span, measure);
    let frag: IdxTok[];
    let exclusive = false;
    if (head.length > 0) {
      frag = head;
      remaining = rest;
    } else {
      frag = [remaining[0]];
      remaining = remaining.slice(1);
      exclusive = true;
    }
    const isLast = remaining.length === 0;
    out.push({
      toks: frag.map((t) => ({ tok: stripSpacingMods(t.tok, isFirst, isLast), src: t.src })),
      exclusive,
    });
    isFirst = false;
  }
  return out;
}

interface QLineIdx {
  toks: IdxTok[];
  exclusive: boolean;
  /** source-LINE index (earliest wins row conflicts) — distinct from each
   *  token's own `.src` origin index. */
  lineSrc: number;
  sig: string;
  spacer: boolean;
}

/** Same guarantees and row-pairing rules as `layoutZones` (see its doc
 *  comment) — mirrored here token-for-token so the two can never disagree
 *  on a wrap decision — plus a parallel `rowsSrc` giving every rendered
 *  token fragment's origin index in the caller's raw arrays. */
export function layoutZonesIndexed(
  leftTokens: string[],
  centerTokens: string[],
  rightTokens: string[],
  w: number,
  measure: ZoneMeasure,
  gap: number = ZONE_GAP_TWIPS
): { rows: ZoneRow[]; rowsSrc: ZoneRowSrc[] } {
  const tag = (arr: string[]): IdxTok[] => arr.map((tok, src) => ({ tok, src }));
  const leftLines = splitIdxTokenLines(normalizeIdxTokens(tag(leftTokens)));
  const centerLines = splitIdxTokenLines(normalizeIdxTokens(tag(centerTokens)));
  const rightLines = splitIdxTokenLines(normalizeIdxTokens(tag(rightTokens)));
  const srcLines = Math.max(leftLines.length, centerLines.length, rightLines.length);
  if (srcLines === 0) return { rows: [], rowsSrc: [] };

  const qL: QLineIdx[] = [];
  const qC: QLineIdx[] = [];
  const qR: QLineIdx[] = [];
  const enqueue = (q: QLineIdx[], vis: VisLineIdx[], lineSrc: number) =>
    vis.forEach((v) =>
      q.push({
        toks: v.toks,
        exclusive: v.exclusive,
        lineSrc,
        sig: spacingSig(v.toks.map((t) => t.tok)),
        spacer: false,
      })
    );
  const spacer = (lineSrc: number): QLineIdx => ({
    toks: [],
    exclusive: false,
    lineSrc,
    sig: '',
    spacer: true,
  });

  for (let i = 0; i < srcLines; i++) {
    const L = leftLines[i] || [];
    const C = centerLines[i] || [];
    const R = rightLines[i] || [];
    const hasC = C.length > 0;

    let visL: VisLineIdx[] = [];
    let visC: VisLineIdx[] = [];
    let visR: VisLineIdx[] = [];
    if (hasC && L.length === 0 && R.length === 0) {
      visC = wrapIdxLine(C, w, measure);
    } else if (hasC) {
      const minCenter = Math.floor(w * MIN_CENTER_SHARE);
      const capOuter = Math.max(Math.floor((w - minCenter) / 2) - gap, Math.floor(w * 0.15));
      visL = wrapIdxLine(L, capOuter, measure);
      visR = wrapIdxLine(R, capOuter, measure);
      const widest = (vis: VisLineIdx[]) =>
        vis.reduce((m, v) => (v.exclusive ? m : Math.max(m, measure(v.toks.map((t) => t.tok)))), 0);
      const outerMax = Math.max(widest(visL), widest(visR));
      const centerSpan = w - 2 * (outerMax + gap);
      visC = wrapIdxLine(C, centerSpan, measure);
    } else {
      const Lw = measure(L.map((t) => t.tok));
      const Rw = measure(R.map((t) => t.tok));
      const half = Math.floor((w - gap) / 2);
      const capL = L.length ? Math.max(half, w - gap - Math.min(Rw, half)) : 0;
      const capR = R.length ? Math.max(half, w - gap - Math.min(Lw, half)) : 0;
      visL = wrapIdxLine(L, capL, measure);
      visR = wrapIdxLine(R, capR, measure);
    }

    if (L.length) enqueue(qL, visL, i);
    else if (i < leftLines.length) qL.push(spacer(i));
    if (C.length) enqueue(qC, visC, i);
    else if (i < centerLines.length) qC.push(spacer(i));
    if (R.length) enqueue(qR, visR, i);
    else if (i < rightLines.length) qR.push(spacer(i));
  }

  const rows: { L: IdxTok[]; C: IdxTok[]; R: IdxTok[] }[] = [];
  while (qL.length > 0 || qC.length > 0 || qR.length > 0) {
    if (qL.length > 0 && qL[0].exclusive) {
      rows.push({ L: qL.shift()!.toks, C: [], R: [] });
      continue;
    }
    if (qC.length > 0 && qC[0].exclusive) {
      rows.push({ L: [], C: qC.shift()!.toks, R: [] });
      continue;
    }
    if (qR.length > 0 && qR[0].exclusive) {
      rows.push({ L: [], C: [], R: qR.shift()!.toks });
      continue;
    }

    type Cand = { col: 'L' | 'C' | 'R'; q: QLineIdx[]; e: QLineIdx };
    let cands: Cand[] = [];
    if (qL.length > 0) cands.push({ col: 'L', q: qL, e: qL[0] });
    if (qC.length > 0) cands.push({ col: 'C', q: qC, e: qC[0] });
    if (qR.length > 0) cands.push({ col: 'R', q: qR, e: qR[0] });

    const sigsOf = (list: Cand[]) => new Set(list.filter((c) => !c.e.spacer).map((c) => c.e.sig));
    if (sigsOf(cands).size > 1) {
      const minSrc = Math.min(...cands.map((c) => c.e.lineSrc));
      const lead =
        cands.find((c) => c.e.lineSrc === minSrc && !c.e.spacer) || cands.find((c) => !c.e.spacer)!;
      cands = cands.filter((c) => c.e.spacer || c.e.sig === lead.e.sig);
    }

    const fits = (list: Cand[]) => {
      const occ = list.filter((c) => c.e.toks.length > 0);
      if (occ.length <= 1) return true;
      const total =
        occ.reduce((s, c) => s + measure(c.e.toks.map((t) => t.tok)), 0) + (occ.length - 1) * gap;
      return total <= w;
    };
    if (!fits(cands)) {
      const minSrc = Math.min(...cands.map((c) => c.e.lineSrc));
      cands = cands.filter((c) => c.e.lineSrc === minSrc);
      if (!fits(cands)) {
        const kept: Cand[] = [];
        for (const c of cands) if (fits([...kept, c])) kept.push(c);
        cands = kept.length > 0 ? kept : [cands[0]];
      }
    }

    const row: { L: IdxTok[]; C: IdxTok[]; R: IdxTok[] } = { L: [], C: [], R: [] };
    for (const c of cands) row[c.col] = c.q.shift()!.toks;
    rows.push(row);
  }

  return {
    rows: rows.map((r) => ({
      L: r.L.map((t) => t.tok),
      C: r.C.map((t) => t.tok),
      R: r.R.map((t) => t.tok),
    })),
    rowsSrc: rows.map((r) => ({
      L: r.L.map((t) => t.src),
      C: r.C.map((t) => t.src),
      R: r.R.map((t) => t.src),
    })),
  };
}
